import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { SG_GET_STAFF_ACCESS_LOGS, SG_INVALIDATE_STAFF_CACHE, SG_GET_STAFF_CACHE_STATUS } from 'constants/actions';
import { useState } from 'react';

export default function ReportDetails() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const data = useSelector((s) => s.staff_reports || {});
  const reports = Array.isArray(data.reports?.reports) ? data.reports.reports : (Array.isArray(data.reports) ? data.reports : []);
  const report = reports.find((r) => String(r.id) === String(id)) || null;

  const [logsPage, setLogsPage] = useState(1);
  const [logsPageSize, setLogsPageSize] = useState(25);
  const [eventFilter, setEventFilter] = useState('');

  useEffect(() => {
    if (!id) return;
    const qs = `page=${logsPage}&page_size=${logsPageSize}${eventFilter ? `&event_type=${encodeURIComponent(eventFilter)}` : ''}`;
    dispatch({ type: SG_GET_STAFF_ACCESS_LOGS, payload: { id, query: qs } });
  }, [dispatch, id, logsPage, logsPageSize, eventFilter]);

  // When the report object is available, auto-fetch the cache status for its org + audit
  useEffect(() => {
    if (!report) return;
    if (!report.organization_id || !report.audit_type_id) return;
    const q = `organization_id=${report.organization_id}&audit_type_id=${report.audit_type_id}`;
    // dispatching so the reducer will fill `data.cache_status` and UI will show friendly status
    dispatch({ type: SG_GET_STAFF_CACHE_STATUS, payload: q });
  }, [dispatch, report]);

  const logs = data.access_logs || null;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0 }}>{report?.cache_key ?? `Report ${id}`}</h2>
          <div style={{ color: '#666', fontSize: 13 }}>ID: <span style={{ fontFamily: 'monospace' }}>{id}</span></div>
        </div>
        <div><Link to="..">Back</Link></div>
      </div>

      <div style={{ marginTop: 12, padding: 12, border: '1px solid #eee', borderRadius: 6 }}>
        <h4 style={{ marginTop: 0 }}>Summary</h4>
        <div>Organization: {report?.organization_id ?? '-'}</div>
        <div>Employee: {report?.employee_id ?? '-'}</div>
        <div>Generated: {report?.generated_at ?? '-'}</div>
        <div>Expires: {report?.expires_at ?? '-'}</div>
      </div>

      <div style={{ marginTop: 12 }}>
        <h4>Access logs</h4>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 12 }}>Event</span>
            <input aria-label="Filter by event type" placeholder="Filter by event_type" value={eventFilter} onChange={(e) => setEventFilter(e.target.value)} />
          </label>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}></label>
            <span style={{ fontSize: 12 }}>Per page</span>
            <select aria-label="Logs per page" value={logsPageSize} onChange={(e) => setLogsPageSize(Number(e.target.value))}>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          <button onClick={() => { setLogsPage(1); }}>Apply</button>
        </div>
        {logs && logs.logs && logs.logs.length ? (
            <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {logs.logs.map((l) => (
              <div key={l.id} style={{ padding: 8, border: '1px solid #eee', borderRadius: 6 }}>
                <div>{new Date(l.accessed_at).toLocaleString()} — {l.event_type} — emp {l.employee_id}</div>
                <div style={{ color: '#666', fontSize: 12 }}>Response time: {l.response_time_ms ? `${l.response_time_ms} ms` : '-'}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
            <button aria-label="Previous logs page" onClick={() => setLogsPage(Math.max(1, logsPage - 1))} disabled={logsPage <= 1}>Prev</button>
            <span>Page {logsPage}</span>
            <button aria-label="Next logs page" onClick={() => setLogsPage(logsPage + 1)} disabled={logs && logs.meta && logs.meta.total && logsPage >= Math.ceil(logs.meta.total / logsPageSize)}>Next</button>
          </div>
          </>
        ) : (
          <div style={{ color: '#666' }}>No access logs yet</div>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <h4>Cache controls</h4>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button aria-label="Invalidate cache for this report" onClick={() => {
            if (!report) return;
            if (!window.confirm('Invalidate cache for this report? This will remove cached results.')) return;
            const q = `organization_id=${report.organization_id}&audit_type_id=${report.audit_type_id}`;
            dispatch({ type: SG_INVALIDATE_STAFF_CACHE, payload: q });
          }}>Invalidate cache</button>

          <button aria-label="Refresh cache status for this report" onClick={() => {
            if (!report) return;
            const q = `organization_id=${report.organization_id}&audit_type_id=${report.audit_type_id}`;
            dispatch({ type: SG_GET_STAFF_CACHE_STATUS, payload: q });
          }}>Refresh status</button>

          <div aria-live="polite" style={{ color: '#666', fontSize: 13 }}>
            {data.cache_status ? (
              (() => {
                const cs = data.cache_status;
                // human readable status
                const cached = cs.cached === true || cs.cached === 'true';
                const active = cs.active === true || cs.is_active === true || cs.is_expired === false;
                const expired = cs.expires_at && (new Date(cs.expires_at) < new Date());
                return (
                  <div>
                    <div><strong>Status:</strong> {cached ? (active ? 'Cached — Active' : (expired ? 'Cached — Expired' : 'Cached — Inactive')) : 'Not cached'}</div>
                    {cs.generated_at && <div>Generated: {cs.generated_at}</div>}
                    {cs.expires_at && <div>Expires: {cs.expires_at}</div>}
                    {cs.total_variants !== undefined && <div>Variants: {cs.total_variants}</div>}
                    {cs.access_count !== undefined && <div>Access count: {cs.access_count}</div>}
                    {cs.task_id && <div>Task ID: {cs.task_id}</div>}
                  </div>
                );
              })()
            ) : (
              <div>
                {/* <strong>Status:</strong> unknown — use "Check cache status" to fetch status for this report.
                {logs && logs.logs && logs.logs.length > 0 ? (
                  <div style={{ marginTop: 6, color: '#555' }}>Note: access logs show {logs.logs.length} recent events ({logs.logs[0].event_type}). This indicates cache activity.</div>
                ) : null} */}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
